/**
 * Adapt the thin {@code formFlowApiClient} into an adapter that satisfies the shared
 * {@link import('./coreFormAdapterTypes').CoreFormAdapter} contract consumed by
 * {@code useFormAnswers}. Lets {@code useFormFlow} reuse every answer-side behaviour
 * (autosave, localStorage backup, upload/remove/retry, saveDraft button, `_file`
 * stripping) without re-implementing it.
 *
 * The adapter closes over a `getCurrentComponentId` getter — each method reads the
 * current step at call time rather than at construction time, so flow step transitions
 * don't require adapter recreation (avoids a re-run of the `useFormAnswers` effects
 * that watch adapter identity).
 *
 * @param {Object} api
 *   The return of {@code createFormFlowApi(formSessionId, { test, grantToken })}.
 * @param {() => string|null} getCurrentComponentId
 *   Reads the session's current step componentId at call time. Returning null fails the
 *   call (upload/delete refuse to run without a target step).
 * @returns {import('./coreFormAdapterTypes').CoreFormAdapter & {
 *   getSession: Function,
 *   submit: Function,
 *   back: Function,
 *   streamUrl: Function,
 * }}
 */
export function createFlowStepAdapter(api, getCurrentComponentId) {
  return {
    capabilities: {
      canUpload: true,
      canSaveDraft: true,
    },

    // ── CoreFormAdapter ────────────────────────────────────────────────────────────
    async saveDraft(answers) {
      const componentId = getCurrentComponentId();
      if (!componentId) return {};
      await api.saveDraft(componentId, answers);
      // api.saveDraft returns 204 No Content; return an empty object so the core hook's
      // post-processing branches (data?.form, data?.validation, data?.instanceId) all
      // resolve to no-ops rather than crashing on null reads.
      return {};
    },

    async upload(fieldKey, file) {
      const componentId = getCurrentComponentId();
      if (!componentId) {
        throw new Error('No current step — cannot upload.');
      }
      return api.upload(componentId, fieldKey, file);
    },

    async deleteFile(fieldKey, fileId) {
      const componentId = getCurrentComponentId();
      if (!componentId) return;
      await api.deleteFile(componentId, fieldKey, fileId);
    },

    // ── Flow-only extensions (not in CoreFormAdapter) ──────────────────────────────
    async getSession() {
      return api.getSession();
    },

    async submit(componentId, answers) {
      return api.submit(componentId, answers);
    },

    async back(targetComponentId) {
      return api.back(targetComponentId);
    },

    streamUrl() {
      return api.streamUrl();
    },
  };
}
